import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  Popover,
  Avatar,
  Chip,
  Button,
  Stack,
  CardMedia,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useLocation } from "react-router-dom";
import httpRequest from "../app/httpRequest";
import db from "../app/db";
import KeosNotificationWidget from "./KeosNotificationWidget";

// Utility function to format timestamp
export const formatTimeAgo = (timestamp, format = "timeAgo") => {
  // Handle invalid or missing timestamp
  if (!timestamp || isNaN(timestamp)) {
    return "Invalid time";
  }

  // Determine if timestamp is in seconds (10 digits) or milliseconds (13 digits)
  const timestampStr = timestamp.toString();
  const isSeconds = timestampStr.length <= 11; // Seconds: ~10 digits, Milliseconds: ~13 digits
  const timestampMs = isSeconds ? timestamp * 1000 : timestamp;

  const date = new Date(timestampMs);
  const now = new Date();

  // Calculate difference in seconds
  const diffInSeconds = Math.floor((now - date) / 1000);

  // Handle future timestamps
  if (diffInSeconds < 0) {
    return "In the future";
  }

  // Format options
  switch (format) {
    case "timeAgo":
      if (diffInSeconds < 60) {
        return `${diffInSeconds} sec ago`;
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} min ago`;
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours} hour${hours > 1 ? "s" : ""} ago`;
      } else {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days} day${days > 1 ? "s" : ""} ago`;
      }
    case "fullDate":
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    case "shortDate":
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    default:
      return date.toLocaleString("en-US");
  }
};

const notifications = [];

const KeosInboxNotification = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [filter, setFilter] = useState("all");
  const [notificationList, setNotificationList] = useState(notifications);
  const [unreadCount, setUnreadCount] = useState(notificationList.filter((n) => !n.read).length);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [clientTopic] = useState(queryParams.get("client_topic"));
  const [clientToken] = useState(queryParams.get("client_token"));
  const [clientId] = useState(queryParams.get("client_id"));
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [latestNotification, setLatestNotification] = useState(null);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const filteredNotifications = notificationList.filter((notification) => {
    if (filter === "all") return true;
    return filter === "unread" ? !notification.read : notification.read;
  });

  const handleToggleRead = (id) => {
    try {
      const updatedNotifications = notificationList.map((n) => {
        return n.id === id ? { ...n, read: !n.read } : n;
      });
      setNotificationList(updatedNotifications);
      setUnreadCount(updatedNotifications.filter((n) => !n.read).length);
      (async function () {
        const n = await db().notifications.get(id);
        const nBool = Boolean(n.new);
        db().notifications.update(id, { new: !nBool ? 1 : 0 });
      })();
    } catch (error) {
      console.log("ERROR TOGGLING READ STATE:", error.toString());
    }
  };

  React.useEffect(() => {
    localStorage.setItem("user", "ntfy_inbox");
    if ("Notification" in window) {
      Notification.requestPermission()
        .then(async (permission) => {
          if (permission === "granted") {
            setPermissionGranted(true);
            const sseUrl = `${window.config.base_url}/${clientTopic}/sse?sub_client_token=${clientToken}&client_id=${clientId}`;
            const jsonUrl = `${window.config.base_url}/${clientTopic}/json?sub_client_token=${clientToken}&client_id=${clientId}&poll=1`;
            const cacheReponse = await httpRequest("GET", `${jsonUrl}`);
            let cacheMsgs = [];

            try {
              const dbNotes = await db().notifications.toArray();
              console.log(
                String(cacheReponse.data).split("\n").length,
                "[CACHE RESPONSE FROM SERVER LENGTH]",
                Array.from(dbNotes).length,
                "BROWSER DB NOTES LENGTH "
              );

              const serverMsgs = String(cacheReponse.data)
                .split("\n")
                .map((msgData) => {
                  try {
                    const msg = JSON.parse(msgData.trim());
                    msg["new"] = 1;
                    return Array.isArray(dbNotes) && !dbNotes.some((n) => n.id == msg.id) ? msg : undefined;
                  } catch (error) {
                    console.error(new Error(`PARSING SERVER MESSAGE ERROR: ${error.toString()}`));
                  }
                })
                .filter((msg) => {
                  // "Do something else here"
                  return msg != undefined;
                });

              await db().notifications.bulkAdd(serverMsgs);
            } catch (error) {
              console.log("DB UPDATE ERROR", error.toString());
            } finally {
              cacheMsgs = Array.from(await db().notifications.toArray())
                .map((msg) => ({ ...msg, read: !Boolean(msg.new) }))
                .filter((msg) => msg.read == false);
            }
            setNotificationList(cacheMsgs);
            const eventSource = new EventSource(`${sseUrl}`);
            eventSource.onmessage = async function (event) {
              try {
                const data = event.data;
                let newNote = { ...JSON.parse(data), read: false };
                await db().notifications.put({ ...newNote, new: 1 });
                setNotificationList((current) => [...current, newNote]);
                setLatestNotification(newNote); // Add this line
                new Notification("🔔 Notification", { body: data });
              } catch (error) {
                console.log(error);
                console.log(`ERROR ADDING NEW BACKGROUND MESSAGE: ${error.toString()}`);
              }
            };
          }
        })
        .catch((err) => {
          console.log("Notification subscription error ", err);
        });
    }
  }, []);

  useEffect(() => {
    setUnreadCount(notificationList.filter((n) => !n.read).length);
  }, [notificationList]);
  // Query DB

  const NotificationListItem = ({ notification }) => {
    return (
      <ListItem
        sx={{
          bgcolor: notification.read ? "background.paper" : "#f8fafc",
          "&:hover": { bgcolor: "#f1f5f9" },
          cursor: "pointer",
          alignItems: "flex-start",
          py: 1.5,
          px: 2,
          transition: "all 0.2s ease",
          borderLeft: notification.read ? "none" : "3px solid #3b82f6",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top Row - Avatar + Title + Time */}
        <Box sx={{ display: "flex", width: "100%", mb: 1 }}>
          {/* Avatar */}
          <Box
            sx={{
              width: 40,
              height: 40,
              minWidth: 40,
              borderRadius: "50%",
              overflow: "hidden",
              mr: 2,
              bgcolor: "#e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {notification.attachment?.url ? (
              <CardMedia
                component="img"
                image={notification.attachment.url}
                alt={notification.attachment.name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Typography
                variant="subtitle1"
                sx={{
                  color: "#64748b",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                }}
              >
                {notification.title?.charAt(0) || "N"}
              </Typography>
            )}
          </Box>

          {/* Title + Time */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight={notification.read ? "500" : "600"}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: notification.read ? "#64748b" : "#1e293b",
              }}
            >
              {notification.title}
            </Typography>
            <Typography
              variant="caption"
              color="#94a3b8"
              sx={{
                fontSize: "0.7rem",
              }}
            >
              {formatTimeAgo(notification.time)}
            </Typography>
          </Box>

          {/* Read/Unread Toggle */}
          <IconButton
            size="small"
            onClick={() => handleToggleRead(notification.id)}
            sx={{
              p: 0.5,
              color: notification.read ? "#94a3b8" : "#3b82f6",
              alignSelf: "flex-start",
            }}
          >
            {notification.read ? <MarkEmailUnreadIcon fontSize="small" /> : <MarkEmailReadIcon fontSize="small" />}
          </IconButton>
        </Box>

        {/* Message Content */}
        <Typography
          variant="body2"
          sx={{
            mb: 1.5,
            fontSize: "0.875rem",
            lineHeight: 1.4,
            color: "#475569",
            width: "100%",
            px: 0.5,
          }}
        >
          {notification.message}
        </Typography>

        {/* Tags */}
        {notification.tags?.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              mb: 1.5,
              width: "100%",
            }}
          >
            {notification.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: "0.65rem",
                  borderRadius: 4,
                  borderColor: "#e2e8f0",
                  color: "#475569",
                  height: "20px",
                  bgcolor: "#f1f5f9",
                }}
              />
            ))}
          </Box>
        )}

        {/* Actions - Horizontal List */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            width: "100%",
            borderTop: "1px solid #e2e8f0",
            pt: 1,
          }}
        >
          {notification.click && (
            <Button
              variant="text"
              size="small"
              startIcon={<OpenInNewIcon fontSize="small" />}
              onClick={(e) => {
                e.stopPropagation();
                window.open(notification.click, "_blank");
              }}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                px: 1,
                py: 0.25,
                color: "#3b82f6",
                "&:hover": {
                  bgcolor: "rgba(59, 130, 246, 0.1)",
                },
              }}
            >
              View
            </Button>
          )}

          {notification.actions?.map((action) => (
            <Button
              key={action.id}
              variant="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                window.open(action.url, "_blank");
              }}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                px: 1,
                py: 0.25,
                color: "#3b82f6",
                "&:hover": {
                  bgcolor: "rgba(59, 130, 246, 0.1)",
                },
              }}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      </ListItem>
    );
  };

  if (!permissionGranted) {
    return <></>;
  }
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          bgcolor: "background.paper",
          boxShadow: 3,
          "&:hover": { bgcolor: "background.default" },
          width: 48,
          height: 48,
        }}
        onClick={handleOpen}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          overlap="circular"
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <NotificationsIcon color="primary" />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: { xs: "90vw", sm: 400 },
            maxHeight: "80vh",
            borderRadius: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="medium">
            Notifications
          </Typography>
          <Select
            value={filter}
            onChange={handleFilterChange}
            size="small"
            sx={{
              color: "primary.contrastText",
              ".MuiSvgIcon-root": { color: "primary.contrastText" },
              ".MuiOutlinedInput-notchedOutline": { borderColor: "primary.contrastText" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.contrastText",
              },
              fontSize: "0.875rem",
              height: 32,
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 1,
                },
              },
            }}
            IconComponent={ArrowDropDownIcon}
          >
            <MenuItem value="all" dense>
              All
            </MenuItem>
            <MenuItem value="unread" dense>
              Unread
            </MenuItem>
            <MenuItem value="read" dense>
              Read
            </MenuItem>
          </Select>
        </Box>

        {/* Notification List */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "divider",
              borderRadius: "3px",
            },
          }}
        >
          <List sx={{ p: 0 }}>
            {filteredNotifications.length === 0 ? (
              <ListItem sx={{ py: 3 }}>
                <ListItemText
                  primary={
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      No notifications
                    </Typography>
                  }
                />
              </ListItem>
            ) : (
              filteredNotifications.map((notification) => <NotificationListItem key={notification.id} notification={notification} />)
            )}
          </List>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            textAlign: "right",
          }}
        >
          <Button
            variant="text"
            size="small"
            onClick={handleClose}
            sx={{
              textTransform: "none",
              color: "text.secondary",
              fontSize: "0.875rem",
              "&:hover": {
                color: "primary.main",
                bgcolor: "transparent",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Popover>

      {/* Notification Widget */}
      {latestNotification && (
        <KeosNotificationWidget
          notification={latestNotification}
          onClose={() => setLatestNotification(null)}
          onMarkRead={handleToggleRead}
        />
      )}
    </>
  );
};

export default KeosInboxNotification;
